export interface HttpError extends Error {
  status?: number;
  body?: unknown;
}

import { API_BASE_URL } from '@/config/apiBaseUrl';

type QueryParamPrimitive = string | number | boolean;
type QueryParamValue = QueryParamPrimitive | QueryParamPrimitive[] | undefined | null;

function buildUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/')) return `${API_BASE_URL}${path}`;
  return `${API_BASE_URL}/${path}`;
}

async function parseJsonSafe(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function httpGet<T>(path: string, params?: Record<string, QueryParamValue>): Promise<T> {
  const url = new URL(buildUrl(path));
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) {
        v.forEach((item) => url.searchParams.append(k, String(item)));
        return;
      }
      url.searchParams.set(k, String(v));
    });
  }

  const token = localStorage.getItem('auth_token');

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      Accept: 'application/ld+json, application/json',
    },
  });

  if (!res.ok) {
    const err: HttpError = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    err.body = await parseJsonSafe(res);
    throw err;
  }

  return (await res.json()) as T;
}

export async function httpPost<TResponse, TBody extends Record<string, unknown>>(
  path: string,
  body: TBody
): Promise<TResponse> {
  const token = localStorage.getItem('auth_token');

  const res = await fetch(buildUrl(path), {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
      Accept: 'application/ld+json, application/json',
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err: HttpError = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    err.body = await parseJsonSafe(res);
    throw err;
  }

  return (await res.json()) as TResponse;
}

export async function httpPatch<TResponse, TBody extends Record<string, unknown>>(
  path: string,
  body: TBody
): Promise<TResponse> {
  const token = localStorage.getItem('auth_token');

  const res = await fetch(buildUrl(path), {
    method: 'PATCH',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // API Platform supports JSON Merge Patch by default
      'Content-Type': 'application/merge-patch+json',
      Accept: 'application/ld+json, application/json',
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err: HttpError = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    err.body = await parseJsonSafe(res);
    throw err;
  }

  return (await res.json()) as TResponse;
}

export async function httpDelete(path: string): Promise<void> {
  const token = localStorage.getItem('auth_token');

  const res = await fetch(buildUrl(path), {
    method: 'DELETE',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      Accept: 'application/ld+json, application/json',
    },
  });

  if (!res.ok) {
    const err: HttpError = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    err.body = await parseJsonSafe(res);
    throw err;
  }
}

